import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../../services/api';
import { StatusBadge, LoadingSpinner } from '../../components/UI';
import { format } from 'date-fns';

const STATUS_OPTIONS = [
  { value: '',          label: 'All Status' },
  { value: 'pending',   label: '⏳ Pending' },
  { value: 'approved',  label: '✅ Approved' },
  { value: 'forwarded', label: '➡️ Forwarded' },
  { value: 'rejected',  label: '❌ Rejected' },
  { value: 'objection', label: '⚠️ Objection' },
];

const urgencyColor = (days) => {
  if (!days && days !== 0) return '';
  if (days >= 30) return 'bg-red-100 text-red-700 border-red-300';
  if (days >= 14) return 'bg-orange-100 text-orange-700 border-orange-300';
  if (days >= 7)  return 'bg-yellow-100 text-yellow-700 border-yellow-300';
  return 'bg-gray-100 text-gray-600 border-gray-200';
};

const exportCSV = (apps) => {
  const headers = ['App No','Applicant','Mobile','NOC Type','Status','Current Dept','Submitted','Total Days','Pending Days'];
  const rows = apps.map(a => [
    a.application_no, a.applicant_name, a.applicant_mobile || a.user_mobile,
    a.noc_name, a.status, a.current_dept || '—',
    a.submitted_at ? format(new Date(a.submitted_at), 'dd/MM/yyyy') : '—',
    a.total_days || 0,
    a.pending_days ?? '—'
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `dept-noc-report-${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

export default function DeptSummaryReport() {
  const { role } = useSelector(s => s.auth);
  const [apps, setApps]           = useState([]);
  const [deptGroups, setDeptGroups] = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [page, setPage]           = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [depts, setDepts]         = useState([]);
  const [nocTypes, setNocTypes]   = useState([]);
  const [viewMode, setViewMode]   = useState('grouped'); // 'grouped' | 'flat'
  const [filters, setFilters]     = useState({
    dept_id: '', noc_type_id: '', status: '', date_from: '', date_to: ''
  });

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const clean = Object.fromEntries(Object.entries(filters).filter(([,v]) => v));
      const params = new URLSearchParams({ ...clean, page: p, limit: 50 });
      const r = await api.get(`/reports/dept-summary?${params}`);
      setApps(r.applications || []);
      setDeptGroups(r.dept_groups || []);
      setTotal(r.total || 0);
      setPage(p);
      setTotalPages(r.total_pages || 1);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => {
    api.get('/departments/public').then(r => setDepts(r.departments || []));
    api.get('/noc-types/public').then(r => setNocTypes(r.noc_types || []));
    load(1);
  }, []);

  const isFlatRole = ['related_dept','final_dept'].includes(role);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">🏢 Department-wise NOC Report</h1>
          <p className="text-sm text-gray-500 mt-1">
            All applications grouped by department with pending days
          </p>
        </div>
        <div className="flex gap-2 no-print">
          {!isFlatRole && (
            <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
              <button onClick={() => setViewMode('grouped')}
                className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${viewMode === 'grouped' ? 'bg-white shadow text-gray-800' : 'text-gray-500'}`}>
                🏢 Grouped
              </button>
              <button onClick={() => setViewMode('flat')}
                className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${viewMode === 'flat' ? 'bg-white shadow text-gray-800' : 'text-gray-500'}`}>
                📋 Flat
              </button>
            </div>
          )}
          <button onClick={() => window.print()}
            className="text-sm px-4 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-medium no-print">
            🖨️ Print
          </button>
          <button onClick={() => exportCSV(apps)} disabled={apps.length === 0}
            className="text-sm px-4 py-2 rounded-xl bg-green-600 hover:bg-green-700 text-white font-medium disabled:opacity-40 no-print">
            📥 Export CSV
          </button>
        </div>
      </div>

      {/* Stats */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total', val: total, cls: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
            { label: 'Pending', val: apps.filter(a => ['submitted','under_review'].includes(a.status)).length, cls: 'text-yellow-700', bg: 'bg-yellow-50 border-yellow-200' },
            { label: 'Approved', val: apps.filter(a => a.status === 'approved_final').length, cls: 'text-green-700', bg: 'bg-green-50 border-green-200' },
            { label: 'Departments', val: deptGroups.length, cls: 'text-purple-700', bg: 'bg-purple-50 border-purple-200' },
          ].map(s => (
            <div key={s.label} className={`card border ${s.bg} text-center p-3`}>
              <p className={`text-2xl font-bold ${s.cls}`}>{s.val}</p>
              <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="card mb-4 py-3 no-print">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          {!isFlatRole && (
            <select className="form-select text-sm" value={filters.dept_id}
              onChange={e => setFilters(f => ({ ...f, dept_id: e.target.value }))}>
              <option value="">All Departments</option>
              {depts.map(d => <option key={d.id} value={d.id}>{d.dept_name}</option>)}
            </select>
          )}
          <select className="form-select text-sm" value={filters.noc_type_id}
            onChange={e => setFilters(f => ({ ...f, noc_type_id: e.target.value }))}>
            <option value="">All NOC Types</option>
            {nocTypes.map(n => <option key={n.id} value={n.id}>{n.noc_name}</option>)}
          </select>
          <select className="form-select text-sm" value={filters.status}
            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <input type="date" className="form-input text-sm" value={filters.date_from}
            onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} />
          <input type="date" className="form-input text-sm" value={filters.date_to}
            onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} />
          <div className="flex gap-2">
            <button onClick={() => load(1)} className="btn-primary flex-1 text-sm">🔍 Search</button>
            <button onClick={() => { setFilters({ dept_id:'', noc_type_id:'', status:'', date_from:'', date_to:'' }); setTimeout(() => load(1), 100); }}
              className="btn-secondary text-sm px-3">✕</button>
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner size="lg" className="py-20" />
      ) : apps.length === 0 ? (
        <div className="card text-center py-16">
          <p className="text-5xl mb-3">📂</p>
          <p className="text-lg font-semibold text-gray-700">No applications found</p>
        </div>
      ) : viewMode === 'grouped' && !isFlatRole ? (
        /* ── GROUPED VIEW ── */
        <div className="space-y-6">
          {deptGroups.map(group => (
            <div key={group.dept_name} className="card overflow-hidden p-0">
              {/* Dept header */}
              <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-blue-800">🏢 {group.dept_name}</span>
                  {group.dept_code && (
                    <code className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{group.dept_code}</code>
                  )}
                </div>
                <span className="text-sm font-semibold text-blue-700 bg-blue-100 px-3 py-1 rounded-full">
                  {group.count} application(s)
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>App No</th>
                      <th>Applicant</th>
                      <th>Mobile</th>
                      <th>NOC Type</th>
                      <th>Status</th>
                      <th>Submitted</th>
                      <th>Total Days</th>
                      <th>Pending Days</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.applications.map(app => (
                      <tr key={app.id}>
                        <td className="font-mono text-xs text-blue-700 font-semibold">{app.application_no}</td>
                        <td className="text-sm">{app.applicant_name}</td>
                        <td className="text-xs text-gray-500">{app.applicant_mobile || app.user_mobile}</td>
                        <td className="text-sm font-medium">{app.noc_name}</td>
                        <td><StatusBadge status={app.status} /></td>
                        <td className="text-xs text-gray-500">
                          {app.submitted_at ? format(new Date(app.submitted_at), 'dd/MM/yyyy') : '—'}
                        </td>
                        <td className="text-center">
                          <span className="text-xs font-semibold text-gray-600">{app.total_days}d</span>
                        </td>
                        <td className="text-center">
                          {app.pending_days != null ? (
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${urgencyColor(app.pending_days)}`}>
                              {app.pending_days}d
                            </span>
                          ) : <span className="text-gray-300 text-xs">—</span>}
                        </td>
                        <td>
                          <Link to={`/admin/applications/${app.id}`}
                            className="text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded-full hover:bg-blue-100">
                            View →
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ── FLAT VIEW ── */
        <div className="card overflow-hidden p-0">
          <div className="px-4 py-2.5 bg-gray-50 border-b flex items-center justify-between text-xs text-gray-500">
            <span>{apps.length} of {total} applications</span>
            <span>Page {page} of {totalPages}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>App No</th>
                  <th>Applicant</th>
                  <th>Mobile</th>
                  <th>NOC Type</th>
                  <th>Status</th>
                  <th>Current Dept</th>
                  <th>Submitted</th>
                  <th>Total Days</th>
                  <th>Pending Days</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((app, i) => (
                  <tr key={app.id}>
                    <td className="text-xs text-gray-400">{(page-1)*50+i+1}</td>
                    <td className="font-mono text-xs text-blue-700 font-semibold">{app.application_no}</td>
                    <td className="text-sm">{app.applicant_name}</td>
                    <td className="text-xs text-gray-500">{app.applicant_mobile || app.user_mobile}</td>
                    <td className="text-sm font-medium">{app.noc_name}</td>
                    <td><StatusBadge status={app.status} /></td>
                    <td className="text-xs text-gray-500">{app.current_dept || '—'}</td>
                    <td className="text-xs text-gray-400">
                      {app.submitted_at ? format(new Date(app.submitted_at), 'dd/MM/yyyy') : '—'}
                    </td>
                    <td className="text-center">
                      <span className="text-xs font-semibold text-gray-600">{app.total_days}d</span>
                    </td>
                    <td className="text-center">
                      {app.pending_days != null ? (
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${urgencyColor(app.pending_days)}`}>
                          {app.pending_days}d
                        </span>
                      ) : <span className="text-gray-300 text-xs">—</span>}
                    </td>
                    <td>
                      <Link to={`/admin/applications/${app.id}`}
                        className="text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded-full hover:bg-blue-100">
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between no-print">
              <button onClick={() => load(page-1)} disabled={page===1}
                className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-40">← Prev</button>
              <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
              <button onClick={() => load(page+1)} disabled={page===totalPages}
                className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-40">Next →</button>
            </div>
          )}
        </div>
      )}

      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { font-size: 11px; }
        }
      `}</style>
    </div>
  );
}